import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { sendApiRequest } from '../../utils';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  FiDollarSign,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiRefreshCw,
  FiSearch,
  FiFilter,
  FiCalendar,
  FiUser,
  FiMail,
  FiPhone,
  FiBank,
  FiCreditCard,
  FiCode,
  FiMapPin,
  FiEye,
  FiEdit,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiAlertCircle,
  FiTrendingUp
} from 'react-icons/fi';
import {
  FaWallet,
  FaMoneyBillWave,
  FaReceipt,
  FaUserCircle,
  FaRegCalendarAlt,
  FaPiggyBank
} from 'react-icons/fa';

const WithdrawalRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [stats, setStats] = useState({
    counts: { total: 0, pending: 0, approved: 0, rejected: 0 },
    amounts: { total: 0, pending: 0, approved: 0, rejected: 0 }
  });
  const [searchParams, setSearchParams] = useState({
    status: 'all',
    startDate: null,
    endDate: null
  });

  const fetchStats = async () => {
    try {
      const response = await sendApiRequest('api/withdrawal-requests/admin/stats', {}, 'GET');
      if (response.status === 200) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        ...(searchParams.status !== 'all' && { status: searchParams.status }),
        ...(searchParams.startDate && { startDate: searchParams.startDate.toISOString() }),
        ...(searchParams.endDate && { endDate: searchParams.endDate.toISOString() })
      });

      const response = await sendApiRequest(`api/withdrawal-requests/admin?${params}`, {}, 'GET');
      
      if (response.status === 200) {
        setRequests(response.data.withdrawalRequests || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalRecords(response.data.total || 0);
      } else {
        toast.error(response.data?.message || 'Failed to fetch withdrawal requests');
        setRequests([]);
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred while fetching requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [currentPage, itemsPerPage, searchParams.status, searchParams.startDate, searchParams.endDate]);

  const handleStatusUpdate = async (requestId, status, adminNote) => {
    setIsUpdating(true);
    try {
      const response = await sendApiRequest(`api/withdrawal-requests/admin/${requestId}`, { status, adminNote }, 'PUT');
      if (response.status === 200) {
        toast.success('Withdrawal request status updated successfully');
        fetchRequests();
        fetchStats();
        setIsModalOpen(false);
      } else {
        toast.error(response.data?.message || 'Failed to update request status');
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred while updating the request');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSearch = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleDateChange = (date, field) => {
    setSearchParams(prev => ({ ...prev, [field]: date }));
    setCurrentPage(1);
  };

  const openModal = (request) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
  };

  const formatAmount = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} Lakh`;
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusConfig = (status) => {
    switch(status) {
      case 'approved':
        return { 
          icon: <FiCheckCircle className="w-4 h-4" />, 
          color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
          label: 'Approved'
        };
      case 'pending':
        return { 
          icon: <FiClock className="w-4 h-4" />, 
          color: 'bg-amber-100 text-amber-700 border-amber-200',
          label: 'Pending'
        };
      case 'rejected':
        return { 
          icon: <FiXCircle className="w-4 h-4" />, 
          color: 'bg-red-100 text-red-700 border-red-200',
          label: 'Rejected'
        };
      default:
        return { 
          icon: <FiAlertCircle className="w-4 h-4" />, 
          color: 'bg-gray-100 text-gray-700 border-gray-200',
          label: status
        };
    }
  };

  const statsCards = [
    { key: 'total', label: 'Total Requests', icon: <FaReceipt className="w-6 h-6" />, color: 'from-indigo-500 to-purple-500', bgColor: 'from-indigo-50 to-purple-50' },
    { key: 'pending', label: 'Pending', icon: <FiClock className="w-6 h-6" />, color: 'from-amber-500 to-orange-500', bgColor: 'from-amber-50 to-orange-50' },
    { key: 'approved', label: 'Approved', icon: <FiCheckCircle className="w-6 h-6" />, color: 'from-emerald-500 to-teal-500', bgColor: 'from-emerald-50 to-teal-50' },
    { key: 'rejected', label: 'Rejected', icon: <FiXCircle className="w-6 h-6" />, color: 'from-red-500 to-rose-500', bgColor: 'from-red-50 to-rose-50' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Withdrawal Requests
                </h1>
              </div>
              <p className="text-gray-500 mt-1 text-sm">Manage and process withdrawal requests</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchRequests}
                className="p-2.5 bg-white rounded-xl shadow-md hover:shadow-lg transition text-gray-600 hover:text-indigo-600"
                title="Refresh"
              >
                <FiRefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {statsCards.map((card, index) => (
            <motion.div
              key={card.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{card.label}</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.counts[card.key]}</p>
                  <p className="text-xs font-semibold mt-1">{formatAmount(stats.amounts[card.key])}</p>
                  <div className="text-xs text-gray-400 mt-1">
                    Admin Fee: {formatAmount(stats.amounts[card.key] * 0.05)}
                  </div>
                  <div className="text-xs text-gray-400">
                    TDS: {formatAmount(stats.amounts[card.key] * 0.05)}
                  </div>
                </div>
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${card.color} flex items-center justify-center shadow-lg`}>
                  {card.icon}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Search Filters */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-6 overflow-hidden">
          <div className="p-5">
            <div className="flex flex-col sm:flex-row gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select
                  name="status"
                  value={searchParams.status}
                  onChange={handleSearch}
                  className="px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none bg-white"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                <div className="relative">
                  <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <DatePicker
                    selected={searchParams.startDate}
                    onChange={(date) => handleDateChange(date, 'startDate')}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none"
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select start date"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                <div className="relative">
                  <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <DatePicker
                    selected={searchParams.endDate}
                    onChange={(date) => handleDateChange(date, 'endDate')}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none"
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select end date"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-4 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            Showing {requests.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} to{' '}
            {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} entries
          </p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="px-2 py-1 border border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
            <span className="text-sm text-gray-500">entries</span>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-indigo-50 to-purple-50">
                <tr>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">User Details</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Bank Details</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Request Date</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <AnimatePresence>
                  {loading ? (
                    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <td colSpan="6" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="relative">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <FaWallet className="w-5 h-5 text-indigo-400 animate-pulse" />
                            </div>
                          </div>
                          <p className="mt-4 text-gray-500 font-medium">Loading withdrawal requests...</p>
                        </div>
                      </td>
                    </motion.tr>
                  ) : requests.length === 0 ? (
                    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <td colSpan="6" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <FiDollarSign className="w-10 h-10 text-gray-400" />
                          </div>
                          <p className="text-gray-500 font-medium text-lg">No withdrawal requests found</p>
                          <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
                        </div>
                      </td>
                    </motion.tr>
                  ) : (
                    requests.map((request, index) => {
                      const statusConfig = getStatusConfig(request.status);
                      return (
                        <motion.tr
                          key={request._id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ delay: index * 0.02 }}
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => openModal(request)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                                <FaUserCircle className="w-5 h-5 text-indigo-500" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{request.userId?.fullName || 'N/A'}</p>
                                <p className="text-xs text-gray-500">{request.userId?.email}</p>
                                <p className="text-xs text-gray-400">{request.userId?.phone}</p>
                              </div>
                            </div>
                           </td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              <p className="text-sm font-bold text-gray-800">{formatAmount(request.amount)}</p>
                              <div className="text-xs text-gray-500">
                                Admin Fee: {formatAmount(request.deductions?.adminFee)}
                              </div>
                              <div className="text-xs text-gray-500">
                                TDS: {formatAmount(request.deductions?.tds)}
                              </div>
                              <div className="text-sm font-semibold text-emerald-600">
                                Net: {formatAmount(request.deductions?.netAmount)}
                              </div>
                            </div>
                           </td>
                          <td className="px-4 py-3">
                            <div className="space-y-1 text-sm">
                              <p className="flex items-center gap-1"><FaPiggyBank className="w-3 h-3 text-gray-400" /> {request.bankDetails?.bankName}</p>
                              <p className="text-xs text-gray-500">****{request.bankDetails?.accountNumber?.slice(-4)}</p>
                              <p className="text-xs text-gray-500">{request.bankDetails?.ifscCode}</p>
                            </div>
                           </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <FiCalendar className="w-3 h-3 text-gray-400" />
                              {formatDate(request.createdAt)}
                            </div>
                           </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${statusConfig.color}`}>
                              {statusConfig.icon}
                              {statusConfig.label}
                            </span>
                           </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={(e) => { e.stopPropagation(); openModal(request); }}
                              className="p-2 rounded-lg bg-indigo-100 text-indigo-600 hover:bg-indigo-200 transition"
                            >
                              <FiEye className="w-4 h-4" />
                            </button>
                           </td>
                        </motion.tr>
                      );
                    })
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-gray-100 px-6 py-4 bg-gray-50">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">Page {currentPage} of {totalPages}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Update Modal */}
      <AnimatePresence>
        {isModalOpen && selectedRequest && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={closeModal}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 sticky top-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl">
                      <FaWallet className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-white font-semibold text-lg">Update Withdrawal Request</h2>
                      <p className="text-indigo-100 text-sm">Manage request status</p>
                    </div>
                  </div>
                  <button onClick={closeModal} className="text-white/80 hover:text-white hover:bg-white/20 rounded-xl p-2 transition">
                    <FiX className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-5">
                {/* Amount Details */}
                <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
                  <h3 className="text-sm font-semibold text-indigo-800 mb-3">Amount Details</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Requested Amount</span>
                      <span className="font-bold text-gray-800">{formatAmount(selectedRequest.amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Admin Fee (5%)</span>
                      <span className="text-gray-700">{formatAmount(selectedRequest.deductions?.adminFee)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">TDS (5%)</span>
                      <span className="text-gray-700">{formatAmount(selectedRequest.deductions?.tds)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-indigo-200">
                      <span className="font-semibold text-gray-800">Net Amount</span>
                      <span className="text-xl font-bold text-emerald-600">{formatAmount(selectedRequest.deductions?.netAmount)}</span>
                    </div>
                  </div>
                </div>

                {/* Bank Details */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <FaPiggyBank className="w-4 h-4 text-indigo-500" />
                    Bank Account Details
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Bank Name</span>
                      <span className="font-medium text-gray-800">{selectedRequest.bankDetails?.bankName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Account Number</span>
                      <span className="font-mono text-gray-800">{selectedRequest.bankDetails?.accountNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">IFSC Code</span>
                      <span className="font-mono text-gray-800">{selectedRequest.bankDetails?.ifscCode}</span>
                    </div>
                  </div>
                </div>

                {/* Status Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Update Status</label>
                  <select
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none"
                    value={selectedRequest.status}
                    onChange={(e) => setSelectedRequest(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                {/* Admin Note */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Admin Note</label>
                  <textarea
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none resize-none"
                    rows="3"
                    value={selectedRequest.adminNote || ''}
                    onChange={(e) => setSelectedRequest(prev => ({ ...prev, adminNote: e.target.value }))}
                    placeholder="Add notes about this request..."
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleStatusUpdate(selectedRequest._id, selectedRequest.status, selectedRequest.adminNote)}
                    disabled={isUpdating}
                    className="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition disabled:opacity-50"
                  >
                    {isUpdating ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Updating...
                      </>
                    ) : (
                      'Update Status'
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WithdrawalRequests;